/**
 * 门店展示表单组件
 * 
 * 功能说明：
 * - 展示月子中心门店的详细信息
 * - 包含服务理念、地理位置、环境设施等介绍
 * - 集成Swiper轮播组件展示门店环境图片
 * - 支持分页点击切换和自动播放
 * 
 * @author 开发团队
 * @version 1.0.0
 */

import React from 'react'
import { Card, Typography, Divider, Tag, Space } from 'antd'
import { 
  StarOutlined, 
  BankOutlined, 
  EnvironmentOutlined, 
  UserOutlined, 
  HomeOutlined 
} from '@ant-design/icons'
import { Swiper, SwiperSlide } from 'swiper/react'
import { EffectCoverflow, Autoplay, A11y, Pagination } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/effect-coverflow'
import 'swiper/css/pagination'

const { Title, Paragraph, Text } = Typography

// 样式常量
const STYLES = {
  card: {
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    borderRadius: '12px',
    overflow: 'hidden'
  },
  title: {
    color: '#ff6b81',
    fontWeight: 'bold',
    marginBottom: '12px',
    textShadow: '0 2px 4px rgba(255, 107, 129, 0.3)'
  },
  brandTag: {
    fontSize: '14px',
    padding: '4px 12px',
    borderRadius: '16px',
    background: 'linear-gradient(135deg, #ff6b81 0%, #ff7979 100%)',
    color: 'white'
  },
  paragraph: {
    fontSize: '16px',
    lineHeight: 1.6,
    color: '#333',
    marginBottom: '20px'
  },
  iconText: {
    color: '#ff6b81',
    marginRight: '8px'
  },
  highlightText: {
    color: '#ff6b81'
  },
  summaryBox: {
    textAlign: 'center',
    padding: '16px',
    borderRadius: '8px',
    background: '#fff5f5'
  },
  swiperContainer: {
    height: '400px',
    position: 'relative',
    backgroundColor: 'transparent',
    backgroundRepeat: 'no-repeat',
    borderRadius: '8px',
    overflow: 'hidden',
    marginTop: '-110px'
  },
  swiperSlide: {
    width: '75%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    padding: '0px',
    transition: 'transform 0.3s ease'
  }
}

/**
 * 门店表单展示组件
 * 
 * @class StoreForm
 * @extends {React.Component}
 * @param {Object} props - 组件属性
 * @param {Object} props.form - 表单实例
 * @param {Object} props.fields - 字段配置
 * @param {Array} props.fields.default_swiper - 轮播图片数据
 */
class StoreForm extends React.Component {
  constructor(props) {
    super(props)
  }
  /**
   * 渲染组件
   * 
   * @returns {JSX.Element} 门店信息展示卡片
   */
  render() {
    const { form, fields = {} } = this.props
    const { data, default_swiper, query = { default: defaultQuery } } = fields
    const renderData = query && query.default && data.find(item => item.store_id === query.default.store_id) || {}
    const remark = renderData && renderData.remark || ''
    return (
      <>
       <div
        style={{
          padding: '10px 18px 60px 18px'
        }}
       >
        {remark && 
          <Paragraph
            style={{ 
              border: '2px solid #ffffff',
              borderRadius: 12,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              marginBottom: 0,
              padding: '10px 10px 60px 10px'
            }}
          >
            {remark}
          </Paragraph>}
       </div>
     
      {/* Swiper轮播组件区域 - 展示门店环境图片 */}
      {/* 修改Swiper轮播组件相关部分 */}
      {default_swiper && default_swiper.length > 0 && (
        <div style={STYLES.swiperContainer}>
        {/* 背景层 - 用于可能的背景效果 */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }} />
        {/* Swiper主容器 */}
        <div style={{
          position: 'relative',
          zIndex: 1,
          height: '100%'
        }}>
          {/* Swiper轮播组件配置 */}
          <Swiper {...SWIPER_CONFIG}>
            {/* 遍历轮播图片数据生成幻灯片 */}
            {default_swiper.map((card, index) => {
              const uniqueKey = card.id || card.store_id || card.name || card.picture || `slide-${index}`;
              return (
                <SwiperSlide key={uniqueKey} style={STYLES.swiperSlide}>
                  <Card
                    className="dbb-card-page"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      boxShadow: 'none',
                      height: '80%',
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      margin: '6px 0 0 0'
                    }}
                  >
                    <img
                      src={card.url}
                      alt={card.name || '月子中心'}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        height: 'auto',
                        objectFit: 'contain',
                        borderRadius: '8px'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div style={{
                      textAlign: 'center',
                      marginTop: '8px',
                      color: '#333',
                      fontSize: '14px',
                      fontWeight: '500',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      width: '100%'
                    }}>
                      {card.name || '月子中心'}
                    </div>
                  </Card>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      </div>
      )}   
     </>
    )
  }
}

// Swiper配置常量
const SWIPER_CONFIG = {
  effect: "coverflow",
  grabCursor: true,
  centeredSlides: true,
  slidesPerView: "auto",
  spaceBetween: 20,
  coverflowEffect: {
    rotate: 30,
    stretch: 0,
    depth: 200,
    modifier: 1,
    slideShadows: false
  },
  autoplay: {
    delay: 3000,
    disableOnInteraction: false,
    pauseOnMouseEnter: false,
    stopOnLastSlide: false,
    reverseDirection: false,
    waitForTransition: true,
    enabled: true,
  },
  loop: true,
  modules: [EffectCoverflow, Autoplay, A11y, Pagination],
  className: "mySwiper",
  style: { height: '100%', position: 'relative' },
  allowTouchMove: true,
  slideToClickedSlide: true,
  watchSlidesProgress: true,
  resistance: true,
  resistanceRatio: 0.85,
  pagination: {
    clickable: true,
    dynamicBullets: true,
    dynamicMainBullets: 3,
    renderBullet: function (index, className) {
      return '<span class="' + className + '">' + (index + 1) + '</span>';
    }
  }
}

// CSS样式常量
const CSS_STYLES = `
  /* 淡入动画 - 用于标题和总结部分 */
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  /* 滑入动画 - 用于内容段落 */
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* 淡入动画类 - 0.6秒缓出效果 */
  .fade-in {
    animation: fadeIn 0.6s ease-out;
  }

  /* 滑入动画类 - 0.5秒缓出效果，保持最终状态 */
  .slide-in {
    animation: slideIn 0.5s ease-out forwards;
  }

  /* Swiper分页样式自定义 */
  .mySwiper .swiper-pagination {
    position: absolute !important;
    bottom: 10px !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
    z-index: 10 !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
  }

  .mySwiper .swiper-pagination-bullet {
    width: 12px !important;
    height: 12px !important;
    background: rgba(255, 107, 129, 0.3) !important;
    opacity: 1 !important;
    margin: 0 4px !important;
    transition: all 0.3s ease !important;
    border-radius: 50% !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    font-size: 10px !important;
    color: white !important;
    font-weight: bold !important;
    cursor: pointer !important;
    border: none !important;
    outline: none !important;
  }

  .mySwiper .swiper-pagination-bullet-active {
    background: #ff6b81 !important;
    transform: scale(1.2) !important;
    box-shadow: 0 0 10px rgba(255, 107, 129, 0.5) !important;
  }

  .mySwiper .swiper-pagination-bullet:hover {
    background: rgba(255, 107, 129, 0.6) !important;
    transform: scale(1.1) !important;
  }

  /* 动态分页点样式 */
  .mySwiper .swiper-pagination-dynamic .swiper-pagination-bullet {
    transform: scale(0.8) !important;
  }

  .mySwiper .swiper-pagination-dynamic .swiper-pagination-bullet-active {
    transform: scale(1.2) !important;
  }

  .mySwiper .swiper-pagination-dynamic .swiper-pagination-bullet-active-main {
    transform: scale(1.2) !important;
  }

  .mySwiper .swiper-pagination-dynamic .swiper-pagination-bullet-active-prev,
  .mySwiper .swiper-pagination-dynamic .swiper-pagination-bullet-active-next {
    transform: scale(1) !important;
  }
`

// 导出门店表单组件
export default StoreForm